/**
 * Feature Flags System
 *
 * This module provides a comprehensive feature flagging system that works
 * in conjunction with the role-based access control (RBAC) system. It enables
 * fine-grained control over which features are available to which users.
 *
 * Key capabilities:
 * - Feature toggling based on user permissions
 * - Centralized registry of all application features
 * - Dynamic feature enablement/disablement
 * - Feature flag React components for conditional rendering
 * - Higher-order components for feature-gated functionality
 *
 * This system allows the application to:
 * 1. Roll out features gradually
 * 2. A/B test new functionality
 * 3. Provide different feature sets to different user segments
 * 4. Quickly disable problematic features without deploying code
 * 5. Create permission-based feature access
 */

import { PERMISSIONS } from './roles'
import { useRole } from './RoleContext'
import React, { createContext, useContext, useMemo } from 'react'

/**
 * Feature Registry
 *
 * Central repository of all feature flags in the application.
 * Each feature flag defines:
 * - requiredPermissions: Permissions needed to access the feature
 * - description: What the feature does (for documentation)
 * - isActive: Global toggle for the feature (can disable for all users)
 *
 * This registry serves as both documentation of available features
 * and as the control mechanism for feature availability.
 *
 * @type {Object.<string, {requiredPermissions: string[], description: string, isActive: boolean}>}
 */
const featureRegistry = {
  // Content creation features
  CONTENT_CREATION: {
    requiredPermissions: [PERMISSIONS.CREATE_RESOURCES],
    description: 'Ability to create new educational content',
    isActive: true,
  },
  CONTENT_APPROVAL: {
    requiredPermissions: [PERMISSIONS.APPROVE_CONTENT],
    description: 'Ability to approve content created by others',
    isActive: true,
  },

  // Analytics features
  PERSONAL_ANALYTICS: {
    requiredPermissions: [PERMISSIONS.VIEW_PERSONAL_ANALYTICS],
    description: 'View personal learning analytics',
    isActive: true,
  },
  REGIONAL_ANALYTICS: {
    requiredPermissions: [PERMISSIONS.VIEW_REGIONAL_ANALYTICS],
    description: 'View analytics for a region',
    isActive: true,
  },
  GLOBAL_ANALYTICS: {
    requiredPermissions: [PERMISSIONS.VIEW_GLOBAL_ANALYTICS],
    description: 'View platform-wide analytics',
    isActive: true,
  },

  // User management features
  USER_MANAGEMENT: {
    requiredPermissions: [PERMISSIONS.MANAGE_USERS],
    description: 'Ability to manage users and their roles',
    isActive: true,
  },
  USER_PROFILE_VIEW: {
    requiredPermissions: [PERMISSIONS.VIEW_USER_PROFILES],
    description: 'View detailed user profiles',
    isActive: true,
  },

  // Ambassador features
  EVENT_CREATION: {
    requiredPermissions: [PERMISSIONS.CREATE_EVENTS],
    description: 'Create and schedule events',
    isActive: true,
  },
  MENTORING: {
    requiredPermissions: [PERMISSIONS.MENTOR_STUDENTS],
    description: 'Access mentoring tools',
    isActive: true,
  },
  PROMOTE_AMBASSADORS: {
    requiredPermissions: [PERMISSIONS.MANAGE_ROLES],
    description: 'Promote users to ambassador roles',
    isActive: true,
  },

  // Admin features
  SYSTEM_SETTINGS: {
    requiredPermissions: [PERMISSIONS.MANAGE_SYSTEM],
    description: 'Access system configuration',
    isActive: true,
  },
  SCHOOL_MANAGEMENT: {
    requiredPermissions: [PERMISSIONS.MANAGE_SCHOOLS],
    description: 'Manage school partnerships',
    isActive: true,
  },

  // Beta features (can be toggled on/off globally)
  ADVANCED_REPORTING: {
    requiredPermissions: [PERMISSIONS.VIEW_REPORTS],
    description: 'Access advanced reporting tools',
    isActive: false, // Disabled feature example
  },
  AI_TOOLS: {
    requiredPermissions: [PERMISSIONS.USE_AI_TOOLS],
    description: 'Access AI-powered educational tools',
    isActive: true,
  },

  // AI Features (Premium)
  AI_PERSONALIZED_TUTORING: {
    requiredPermissions: [PERMISSIONS.USE_AI_TOOLS],
    description: 'Access to AI-powered personalized tutoring assistant',
    isActive: true,
    isPremium: true,
  },
  AI_STUDY_PLANNER: {
    requiredPermissions: [PERMISSIONS.USE_AI_TOOLS],
    description: 'AI-generated personalized study plans and schedules',
    isActive: true,
    isPremium: true,
  },
  AI_EXAM_PREPARATION: {
    requiredPermissions: [PERMISSIONS.USE_AI_TOOLS],
    description:
      'AI-powered exam preparation with personalized practice questions',
    isActive: true,
    isPremium: true,
  },
  AI_FEEDBACK_SYSTEM: {
    requiredPermissions: [PERMISSIONS.USE_AI_TOOLS],
    description: 'Automated assessment feedback using AI',
    isActive: true,
    isPremium: true,
  },

  // Payment and Subscription Features
  PAYMENT_INTEGRATION: {
    requiredPermissions: [PERMISSIONS.USE_AI_TOOLS], // Using existing permission
    description: 'Access to payment methods including MTN and Orange Money',
    isActive: true,
  },
  PREMIUM_CONTENT_ACCESS: {
    requiredPermissions: [PERMISSIONS.VIEW_PREMIUM_RESOURCES],
    description: 'Access to premium educational content',
    isActive: true,
    isPremium: true,
  },
  COMPETITION_REGISTRATION: {
    requiredPermissions: [PERMISSIONS.ENROLL_COURSES], // Using existing permission
    description: 'Register for academic competitions and events',
    isActive: true,
  },

  // Special feature for troubleshooting permissions
  PERMISSIONS_EXPLORER: {
    requiredPermissions: [PERMISSIONS.ADMIN_ACCESS],
    description: 'Access the permissions exploration tool',
    isActive: true,
  },

  // Competition features
  COMPETITIONS_CREATE: {
    requiredPermissions: [PERMISSIONS.CREATE_COMPETITIONS],
    description: 'Create educational competitions',
    isActive: true,
  },
  COMPETITIONS_MANAGE: {
    requiredPermissions: [PERMISSIONS.MANAGE_COMPETITIONS],
    description: 'Manage existing competitions',
    isActive: true,
  },
  COMPETITIONS_PARTICIPATE: {
    requiredPermissions: [PERMISSIONS.PARTICIPATE_IN_COMPETITIONS],
    description: 'Participate in educational competitions',
    isActive: true,
  },

  // Chat and AI features
  AI_CHAT: {
    requiredPermissions: [PERMISSIONS.ACCESS_AI_ASSISTANT],
    description: 'Access to the AI chat assistant',
    isActive: true,
  },
  ADVANCED_AI_FEATURES: {
    requiredPermissions: [PERMISSIONS.ACCESS_ADVANCED_AI],
    description: 'Access to advanced AI features like custom training',
    isActive: true,
  },
}

/**
 * Feature Flags Context
 *
 * Provides the available features to the entire application based on:
 * 1. The user's permissions
 * 2. The global active state of the feature
 *
 * @type {React.Context}
 */
const FeatureFlagsContext = createContext({
  features: {},
  hasFeature: () => false,
  getAllFeatures: () => ({}),
})

/**
 * Feature Flag Provider Component
 *
 * Determines which features are available to the current user based on their
 * permissions and the global feature flags. Provides this information to all
 * child components through React Context.
 *
 * This provider should be placed high in the component tree, typically near
 * the authentication and role providers, so all components have access to
 * feature information.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with feature context
 * @returns {JSX.Element} Provider component
 */
export const FeatureFlagProvider = ({ children }) => {
  // Get user role information to check permissions
  const { hasPermission } = useRole()

  // Compute available features based on user permissions
  const features = useMemo(() => {
    const available = {}

    // Loop through all features in the registry
    Object.entries(featureRegistry).forEach(([featureKey, featureConfig]) => {
      // Check if the feature is globally enabled
      if (!featureConfig.isActive) {
        available[featureKey] = false
        return
      }

      // Check if the user has ALL required permissions for the feature
      const hasAllRequiredPermissions = featureConfig.requiredPermissions.every(
        (permission) => hasPermission(permission)
      )

      available[featureKey] = hasAllRequiredPermissions
    })

    return available
  }, [hasPermission])

  // Function to check if a feature is available
  const hasFeature = (featureKey) => {
    return features[featureKey] || false
  }

  // Function to get all features with their availability status
  const getAllFeatures = () => {
    const result = {}
    Object.entries(featureRegistry).forEach(([key, config]) => {
      result[key] = {
        available: features[key] || false,
        description: config.description,
        requiredPermissions: config.requiredPermissions,
        isActive: config.isActive,
      }
    })
    return result
  }

  const value = {
    features,
    hasFeature,
    getAllFeatures,
  }

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

/**
 * Custom hook to use feature flags
 *
 * Provides access to feature flag information throughout the application.
 * Components can use this hook to determine if certain features should be
 * available based on the current user's permissions.
 *
 * Example usage:
 * ```jsx
 * const { hasFeature } = useFeatureFlags();
 *
 * return (
 *   <div>
 *     {hasFeature('CONTENT_CREATION') && (
 *       <button>Create New Content</button>
 *     )}
 *   </div>
 * );
 * ```
 *
 * @returns {Object} Feature flags context with hasFeature() and other utilities
 * @throws {Error} If used outside of a FeatureFlagProvider
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext)
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider')
  }
  return context
}

/**
 * Get all features with detailed information
 *
 * This is typically used by admin tools to display all available features
 * and their current status. The function is provided here to make it available
 * outside of components (where the hook can't be used).
 *
 * @returns {Object} All features with their details and availability
 */
export const getAllFeaturesWithDetails = () => {
  return Object.entries(featureRegistry).reduce((result, [key, config]) => {
    result[key] = {
      description: config.description,
      requiredPermissions: config.requiredPermissions,
      isActive: config.isActive,
    }
    return result
  }, {})
}

/**
 * Feature Flag Component
 *
 * A component that conditionally renders its children based on feature availability.
 * It checks if the current user has access to the specified feature and renders
 * either the children or the fallback component.
 *
 * Example usage:
 * ```jsx
 * <FeatureFlag feature="CONTENT_CREATION" fallback={<AccessDenied />}>
 *   <ContentCreationForm />
 * </FeatureFlag>
 * ```
 *
 * @param {Object} props - Component props
 * @param {string} props.feature - The feature key to check
 * @param {React.ReactNode} props.children - Content to show if feature is available
 * @param {React.ReactNode} props.fallback - Content to show if feature is not available
 * @returns {JSX.Element|null} Children if feature is available, fallback otherwise
 */
export const FeatureFlag = ({ feature, children, fallback = null }) => {
  const { hasFeature } = useFeatureFlags()
  return hasFeature(feature) ? children : fallback
}

/**
 * Higher-Order Component for feature-gated components
 *
 * Wraps a component with feature flag checking logic, rendering the component
 * only if the user has access to the specified feature. Otherwise, renders
 * the fallback component.
 *
 * Example usage:
 * ```jsx
 * const ProtectedAnalytics = withFeatureFlag('GLOBAL_ANALYTICS', AnalyticsComponent);
 * // Can be used like: <ProtectedAnalytics someProp={value} />
 * ```
 *
 * @param {string} feature - The feature key to check
 * @param {React.ComponentType} Component - Component to render if feature is available
 * @param {React.ComponentType|null} FallbackComponent - Component to render if feature is unavailable
 * @returns {React.ComponentType} New component with feature checking logic
 */
export const withFeatureFlag = (
  feature,
  Component,
  FallbackComponent = null
) => {
  return (props) => (
    <FeatureFlag
      feature={feature}
      fallback={FallbackComponent && <FallbackComponent {...props} />}
    >
      <Component {...props} />
    </FeatureFlag>
  )
}

export default featureRegistry
